<script setup lang="ts">
import { ElTooltip } from 'element-plus'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

interface Props {
  helpKey?: string
  content?: string
  placement?: 'top' | 'top-start' | 'top-end' | 'bottom' | 'bottom-start' | 'bottom-end' | 'left' | 'left-start' | 'left-end' | 'right' | 'right-start' | 'right-end'
  maxWidth?: number
}

const props = withDefaults(defineProps<Props>(), {
  helpKey: '',
  content: '',
  placement: 'right',
  maxWidth: 420,
})

const { t } = useI18n()
const triggerRef = ref<HTMLElement>()
const hovered = ref(false)
const focused = ref(false)
const pinned = ref(false)

const helpContent = computed(() => props.content || (props.helpKey ? t(props.helpKey) : ''))
const visible = computed(() => Boolean(helpContent.value) && (hovered.value || focused.value || pinned.value))

const popperOptions = {
  strategy: 'fixed',
  modifiers: [
    {
      name: 'flip',
      options: {
        fallbackPlacements: ['left', 'top', 'bottom'],
      },
    },
    {
      name: 'preventOverflow',
      options: {
        padding: 12,
      },
    },
  ],
}

function togglePinned() {
  pinned.value = !pinned.value
}

function close() {
  hovered.value = false
  focused.value = false
  pinned.value = false
}

function handleOutside(event: PointerEvent) {
  if (!pinned.value || triggerRef.value?.contains(event.target as Node))
    return
  close()
}

function handleEscape(event: KeyboardEvent) {
  if (event.key === 'Escape')
    close()
}

onMounted(() => {
  document.addEventListener('pointerdown', handleOutside, true)
  document.addEventListener('keydown', handleEscape, true)
})

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', handleOutside, true)
  document.removeEventListener('keydown', handleEscape, true)
})
</script>

<template>
  <span class="question-help">
    <ElTooltip
      :visible="visible"
      :content="helpContent"
      :placement="placement"
      :disabled="!helpContent"
      :teleported="true"
      :show-after="0"
      :hide-after="0"
      :popper-options="popperOptions"
      :popper-style="{ maxWidth: `${maxWidth}px` }"
      effect="dark"
      popper-class="question-help-popper"
    >
      <button
        ref="triggerRef"
        type="button"
        class="question-help-trigger"
        :aria-label="helpContent"
        :aria-expanded="visible"
        @mouseenter="hovered = true"
        @mouseleave="hovered = false"
        @focus="focused = true"
        @blur="focused = false"
        @click.stop.prevent="togglePinned"
      >
        <FaIcon name="i-ri:question-line" aria-hidden="true" />
      </button>
    </ElTooltip>
  </span>
</template>

<style scoped>
.question-help {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin: 0 4px;
  line-height: 1;
  vertical-align: middle;
}

.question-help-trigger {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  outline: none;
  background: transparent;
  color: #94a3b8;
  font-size: 16px;
  line-height: 1;
  vertical-align: middle;
  cursor: help;
  transition: color 0.16s ease, transform 0.16s ease;
}

.question-help-trigger:hover,
.question-help-trigger:focus-visible,
.question-help-trigger[aria-expanded="true"] {
  color: #64748b;
}

.question-help-trigger:focus-visible {
  box-shadow: 0 0 0 3px rgba(100, 116, 139, 0.18);
}

.question-help-trigger:active {
  transform: scale(0.94);
}
</style>

<style>
.el-button > span:has(> .question-help),
.el-radio-button__inner:has(> .question-help),
.el-radio__label:has(> .question-help),
.el-tag:has(> .question-help) {
  display: inline-flex;
  align-items: center;
}

.question-help-popper.el-popper {
  max-width: min(520px, calc(100vw - 24px));
  padding: 12px 14px;
  border: 0;
  border-radius: 8px;
  background: #1e2430;
  color: #ffffff;
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
  white-space: normal;
  overflow-wrap: anywhere;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.24);
  z-index: 100000 !important;
}

.question-help-popper.el-popper .el-popper__arrow::before {
  border-color: #1e2430;
  background: #1e2430;
}
</style>
